import { accountTitle, campaignTitle, splitCommonPrefix } from './campaign-name.js';
import { afterFees, days, gapMinor, num2 } from './format.js';
import { isSegment } from './revision-labels.js';
export const VERDICT_FILTERS = ['yes', 'unclear', 'no', 'none'];
export function readVerdictFilter(v) {
    return VERDICT_FILTERS.includes(v) ? v : undefined;
}
export function verdictKey(g) {
    return g.answer.verdict ?? 'none';
}
export function verdictCounts(campaigns) {
    const count = { yes: 0, unclear: 0, no: 0, none: 0 };
    const spend = { yes: 0, unclear: 0, no: 0, none: 0 };
    for (const c of campaigns) {
        const k = verdictKey(c);
        count[k] += 1;
        spend[k] += c.spendMinorFull;
    }
    return { all: campaigns.length, count, spend };
}
export function filterByVerdict(rows, v) {
    return v ? rows.filter((r) => verdictKey(r) === v) : [...rows];
}
/** Поиск по названию: без регистра, по любой части. */
export function filterBySearch(rows, query) {
    const q = query.trim().toLowerCase();
    if (!q)
        return [...rows];
    return rows.filter((r) => campaignTitle(r).toLowerCase().includes(q));
}
/** Колонки, видимые по умолчанию (в этом порядке). */
export const CAMPAIGN_BASE_COLUMNS = [
    'name',
    'spend',
    'purchases',
    'cpp',
    'breakeven',
    'roas',
    'chance',
    'gap',
];
export const CAMPAIGN_COLUMN_LABEL = {
    name: 'Кампания',
    spend: 'Расход',
    purchases: 'Покупки',
    cpp: 'Цена покупки',
    breakeven: 'В ноль до',
    roas: 'Вернётся на 1 $',
    chance: 'Шанс',
    gap: 'Не хватает / запас',
    safe: 'Уверенно до',
    h60: '60 дн',
    h90: '90 дн',
    fact: 'Вернулось к сегодня',
    age: 'Возраст покупок',
    coverage: 'Покрытие прогнозом',
};
/**
 * Колонки «Кампаний» для меню «Колонки»: «Кампания» закреплена и всегда
 * видна, справочные (после «Не хватает / запас») по умолчанию скрыты.
 */
export const CAMPAIGN_COLUMN_DEFS = [
    'name',
    'spend',
    'purchases',
    'cpp',
    'breakeven',
    'roas',
    'chance',
    'gap',
    'safe',
    'h60',
    'h90',
    'fact',
    'age',
    'coverage',
].map((id) => ({
    id,
    label: CAMPAIGN_COLUMN_LABEL[id],
    ...(id === 'name' ? { defaultPin: 'left', hideable: false } : {}),
    ...(CAMPAIGN_BASE_COLUMNS.includes(id) ? {} : { defaultVisible: false }),
    ...(id === 'roas' ? { minWidth: 170 } : {}),
}));
export const CAMPAIGN_SORT_KEYS = CAMPAIGN_COLUMN_DEFS.map((d) => d.id);
export const DEFAULT_CAMPAIGN_SORT = { key: 'spend', dir: 'desc' };
/** «Вернётся на 1 $» горизонта после вычетов; нет блока — `null`. */
export function horizonRoas(g, h) {
    const af = afterFees(g.horizons[h]);
    return af.available ? af.roas : null;
}
/** Значение колонки для сортировки (`null` — в конце в любом направлении). */
export function sortValue(g, key, theta) {
    switch (key) {
        case 'name':
            return campaignTitle(g).toLocaleLowerCase('ru');
        case 'spend':
            return g.spendMinorFull;
        case 'purchases':
            return g.purchases;
        case 'cpp':
            return g.cpp;
        case 'breakeven':
            return g.cppBreakevenMinor;
        case 'roas':
            return g.answer.roas3;
        case 'chance':
            // «мало» — без вывода шанс не показывается, в конец.
            return g.answer.verdict === null ? null : g.answer.p;
        case 'gap':
            return g.answer.verdict === null ? null : gapMinor(g, theta);
        case 'safe':
            return g.cppSafeMinor;
        case 'h60':
            return horizonRoas(g, '60');
        case 'h90':
            return horizonRoas(g, '90');
        case 'fact':
            return g.factRoas;
        case 'age':
            return g.avgAgeDays;
        case 'coverage':
            return g.coverage;
    }
}
function isNum(v) {
    return typeof v === 'number' && Number.isFinite(v);
}
/**
 * Сортировка с «пустыми» в конце при любом направлении. Устойчивая: при
 * равенстве — исходный порядок (у отчёта — по расходу).
 */
export function sortBy(rows, value, dir) {
    const sign = dir === 'asc' ? 1 : -1;
    return rows
        .map((row, i) => ({ row, i, v: value(row) }))
        .sort((a, b) => {
        const an = a.v === null || (typeof a.v === 'number' && !isNum(a.v));
        const bn = b.v === null || (typeof b.v === 'number' && !isNum(b.v));
        if (an || bn)
            return an === bn ? a.i - b.i : an ? 1 : -1;
        const c = typeof a.v === 'string' || typeof b.v === 'string'
            ? String(a.v).localeCompare(String(b.v), 'ru')
            : a.v - b.v;
        return c !== 0 ? sign * c : a.i - b.i;
    })
        .map((x) => x.row);
}
export function sortRows(rows, sort, theta) {
    return sortBy(rows, (r) => sortValue(r, sort.key, theta), sort.dir);
}
/**
 * Группы-кабинеты: порядок — по расходу кабинета (убывание), строки внутри —
 * уже отсортированы (`rows` в нужном порядке). Пустые группы не показываются.
 * Название кабинета — из отчёта, затем из справочника, иначе «Кабинет без
 * названия».
 */
export function groupByAccount(report, rows, accountNames = new Map()) {
    const byId = new Map();
    for (const r of rows) {
        const list = byId.get(r.accountId);
        if (list)
            list.push(r);
        else
            byId.set(r.accountId, [r]);
    }
    const perAccount = new Map();
    for (const c of report.campaigns)
        perAccount.set(c.accountId, (perAccount.get(c.accountId) ?? 0) + 1);
    const breakdown = new Map(report.accountsBreakdown.map((a) => [a.accountId, a]));
    const groups = [...byId.entries()].map(([accountId, list]) => {
        const account = breakdown.get(accountId) ?? null;
        const name = account?.accountName ??
            list.find((c) => c.accountName)?.accountName ??
            accountNames.get(accountId) ??
            null;
        return {
            accountId,
            title: accountTitle(name),
            account,
            rows: list,
            matched: list.length,
            accountCampaigns: perAccount.get(accountId) ?? list.length,
        };
    });
    const spendOf = (g) => g.account?.spendMinorFull ?? g.rows.reduce((s, r) => s + r.spendMinorFull, 0);
    return sortBy(groups, spendOf, 'desc');
}
/** Плоский порядок строк, как на экране (для ↑↓ панели кампании). */
export function flatRows(groups) {
    return groups.flatMap((g) => g.rows);
}
/**
 * Идентичность строки — пара (`campaignId`, `revision`): у неразделённой кампании версии нет.
 * Строка-ключ — ТОЛЬКО для внутренних `Map`/`key` (в адрес, ссылки и CSV не уходит).
 */
export const rowKey = (c) => isSegment(c) ? `${c.campaignId}/r${c.revision}` : c.campaignId;
/** Строка `c` — это выбранная пара (id, версия)? Версия не задана — подходит любая строка кампании. */
export const matchesRow = (c, id, revision) => id !== null && c.campaignId === id && (revision === null || (c.revision ?? null) === revision);
/**
 * При `split=revision`: строки одной кампании идут подряд (порядок кампаний — по первому
 * появлению в уже отсортированном списке), внутри кампании — версии по возрастанию.
 */
export function clusterByCampaign(rows) {
    const order = [];
    const by = new Map();
    for (const r of rows) {
        const list = by.get(r.campaignId);
        if (list)
            list.push(r);
        else {
            by.set(r.campaignId, [r]);
            order.push(r.campaignId);
        }
    }
    return order.flatMap((id) => [...by.get(id)].sort((a, b) => (a.revision ?? 0) - (b.revision ?? 0)));
}
/**
 * Страница по строкам кампаний (шапки групп не считаются): на странице
 * повторяются шапки групп для её строк.
 */
export function paginateGroups(groups, page, size) {
    const total = groups.reduce((s, g) => s + g.rows.length, 0);
    const pageCount = Math.max(1, Math.ceil(total / size));
    const p = Math.min(Math.max(1, Math.trunc(page) || 1), pageCount);
    const start = (p - 1) * size;
    const end = start + size;
    const out = [];
    let i = 0;
    for (const g of groups) {
        const from = Math.max(start - i, 0);
        const to = Math.min(end - i, g.rows.length);
        if (from < to)
            out.push({ ...g, rows: g.rows.slice(from, to) });
        i += g.rows.length;
    }
    return { page: p, pageCount, total, groups: out };
}
/** Страница, на которой стоит кампания (чтобы панель открывала видимую строку). */
export function pageOf(groups, campaignId, size, revision = null) {
    const i = flatRows(groups).findIndex((c) => matchesRow(c, campaignId, revision));
    return i < 0 ? null : Math.floor(i / size) + 1;
}
/**
 * Названия с приглушённым общим префиксом — по всем кампаниям кабинета в
 * отчёте (не только видимым: префикс не прыгает от фильтра). Без названия —
 * «Кампания без названия» целиком.
 */
export function namePartsByCampaign(campaigns) {
    const out = new Map();
    const byAccount = new Map();
    for (const c of campaigns) {
        const list = byAccount.get(c.accountId);
        if (list)
            list.push(c);
        else
            byAccount.set(c.accountId, [c]);
    }
    for (const list of byAccount.values()) {
        const named = list.filter((c) => (c.campaignName ?? '').trim() !== '');
        const parts = splitCommonPrefix(named.map((c) => (c.campaignName ?? '').trim()));
        named.forEach((c, i) => out.set(c.campaignId, parts[i]));
        for (const c of list) {
            if (!out.has(c.campaignId))
                out.set(c.campaignId, { dim: '', bold: campaignTitle(c) });
        }
    }
    return out;
}
/**
 * Общий префикс названий с соседними строками группы (как в макете: «S | CBO
 * 05.10 CCAP_21 | » приглушён у подряд идущих копий) — по всему кабинету
 * общего префикса обычно нет. Берётся более длинный из префиксов с соседом
 * сверху и снизу.
 */
export function namePartsByNeighbours(groups) {
    const out = new Map();
    for (const g of groups) {
        const names = g.rows.map((c) => (c.campaignName ?? '').trim());
        g.rows.forEach((c, i) => {
            const name = names[i];
            if (name === '' || isSegment(c)) {
                // строки-версии одной кампании: общий префикс скрыл бы всё название
                out.set(rowKey(c), { dim: '', bold: name === '' ? campaignTitle(c) : name });
                return;
            }
            let best = { dim: '', bold: name };
            for (const j of [i - 1, i + 1]) {
                const other = names[j];
                if (!other)
                    continue;
                const p = splitCommonPrefix([name, other])[0];
                if (p.dim.length > best.dim.length)
                    best = p;
            }
            out.set(rowKey(c), best);
        });
    }
    return out;
}
/**
 * Кампания панели из строки адреса. Роутер разбирает поиск JSON-ом, и
 * длинный числовой ID (`?campaign=120250385318650419`) стал бы числом с
 * потерей точности — читаем сырую строку.
 */
export function campaignFromSearch(searchStr) {
    const raw = new URLSearchParams(searchStr).get('campaign');
    if (!raw)
        return null;
    const v = raw.replace(/^"|"$/g, '');
    return /^\d{1,30}$/.test(v) ? v : null;
}
/** Версия выбранной строки из сырого адреса (`?revision=2`); не целое ≥ 1 — `null`. */
export function revisionFromSearch(searchStr) {
    const raw = new URLSearchParams(searchStr).get('revision')?.replace(/^"|"$/g, '');
    return raw && /^[1-9]\d{0,8}$/.test(raw) ? Number(raw) : null;
}
/**
 * Подпись шапки кабинета: «12 кампаний» — по кабинету целиком (не по
 * странице); при фильтре/поиске — «3 из 12 кампаний».
 */
export function groupCountLabel(g) {
    const all = Math.max(g.accountCampaigns, g.matched);
    return g.matched === all ? campaignsWord(all) : `${g.matched} из ${campaignsWord(all)}`;
}
/** «4 кампании», «1 кампания», «12 кампаний». */
export function campaignsWord(n) {
    const m10 = n % 10;
    const m100 = n % 100;
    const w = m10 === 1 && m100 !== 11
        ? 'кампания'
        : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)
            ? 'кампании'
            : 'кампаний';
    return `${n} ${w}`;
}
/**
 * Подсказка «Вернулось к сегодня»: возраст покупок и факт по горизонтам,
 * только где созрело от 5 покупок.
 */
export function factHint(g) {
    const parts = [`Средний возраст покупок ${days(g.avgAgeDays)}`];
    const horizons = ['14', '30', '60', '90', '120']
        .map((h) => ({ h, f: g.factHorizons[h] }))
        .filter((x) => x.f && x.f.factN >= 5 && x.f.factRoas !== null)
        .map((x) => `${x.h} дн — ${num2(x.f?.factRoas)} (покупок ${x.f?.factN})`);
    if (horizons.length)
        parts.push(`факт: ${horizons.join(' · ')}`);
    else
        parts.push('созревших покупок пока нет');
    return parts.join(' · ');
}
// --- CSV --------------------------------------------------------------------
const dollars = (minor) => typeof minor === 'number' && Number.isFinite(minor) ? Math.round(minor) / 100 : null;
const round2 = (x) => typeof x === 'number' && Number.isFinite(x) ? Math.round(x * 100) / 100 : null;
const percent = (x) => typeof x === 'number' && Number.isFinite(x) ? Math.round(x * 100) : null;
/**
 * Колонки CSV — видимые колонки таблицы (с кабинетом впереди: в файле нет
 * группировки). Деньги — в долларах, «Вернётся на 1 $» — два знака, шанс и
 * покрытие — проценты.
 */
export function csvColumns(visible, theta, accountTitleOf, 
/** `split=revision`: + исходный id кампании, версия, её окно и расход без покупок. */
split = false) {
    const col = (key) => {
        const header = key === 'spend' || key === 'cpp' || key === 'breakeven' || key === 'safe' || key === 'gap'
            ? `${CAMPAIGN_COLUMN_LABEL[key]}, $`
            : key === 'chance' || key === 'coverage'
                ? `${CAMPAIGN_COLUMN_LABEL[key]}, %`
                : key === 'age'
                    ? `${CAMPAIGN_COLUMN_LABEL[key]}, дн`
                    : CAMPAIGN_COLUMN_LABEL[key];
        const value = (c) => {
            switch (key) {
                case 'name':
                    return isSegment(c) && c.revision != null
                        ? `${campaignTitle(c)} · версия ${c.revision}`
                        : campaignTitle(c);
                case 'spend':
                    return dollars(c.spendMinorFull);
                case 'purchases':
                    return c.purchases;
                case 'cpp':
                    return dollars(c.cpp);
                case 'breakeven':
                    return dollars(c.cppBreakevenMinor);
                case 'safe':
                    return dollars(c.cppSafeMinor);
                case 'roas':
                    return round2(c.answer.roas3);
                case 'chance':
                    return c.answer.verdict === null ? null : percent(c.answer.p);
                case 'gap':
                    return c.answer.verdict === null ? null : dollars(gapMinor(c, theta));
                case 'h60':
                    return round2(horizonRoas(c, '60'));
                case 'h90':
                    return round2(horizonRoas(c, '90'));
                case 'fact':
                    return round2(c.factRoas);
                case 'age':
                    return round2(c.avgAgeDays);
                case 'coverage':
                    return percent(c.coverage);
            }
        };
        return { header, value };
    };
    const [first, ...rest] = visible;
    return [
        ...(first ? [col(first)] : []),
        { header: 'Кабинет', value: accountTitleOf },
        ...rest.map(col),
        ...(split
            ? [
                { header: 'campaignId', value: (c) => c.campaignId },
                { header: 'revision', value: (c) => c.revision },
                { header: 'revision_from', value: (c) => c.revisionWindow?.from },
                { header: 'revision_to', value: (c) => c.revisionWindow?.to },
                {
                    header: 'spend_without_purchases',
                    value: (c) => dollars(c.spendWithoutPurchases),
                },
            ]
            : []),
    ];
}
//# sourceMappingURL=campaigns-logic.js.map